const React = require('react');
const { createElement: h, useEffect, useMemo, useState, useCallback } = React;
const { loadNodeLogoCatalog } = require('../lib/workflowStorage');
require('./LogoPickerPopup.css');

var LOCAL_KEY = 'alleflow:customLogoUrls';
var URL_REGEX = /^https?:\/\/.+/i;

function readLocalUrls() {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    var raw = window.localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    var parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(function(u) { return typeof u === 'string' && URL_REGEX.test(u); }) : [];
  } catch (_e) {
    return [];
  }
}

function writeLocalUrls(urls) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(urls.slice(0, 100)));
  } catch (_e) {
    /* ignore quota */
  }
}

function dedupe(list) {
  var seen = Object.create(null);
  var out = [];
  for (var i = 0; i < list.length; i++) {
    var url = String(list[i] || '').trim();
    if (!url || seen[url]) continue;
    seen[url] = true;
    out.push(url);
  }
  return out;
}

function collectInMemoryUrls(workflowState, customObjectTemplates) {
  var urls = [];
  var nodes = workflowState && Array.isArray(workflowState.nodes) ? workflowState.nodes : [];
  for (var i = 0; i < nodes.length; i++) {
    if (nodes[i] && nodes[i].logoUrl) urls.push(nodes[i].logoUrl);
  }
  var templates = Array.isArray(customObjectTemplates) ? customObjectTemplates : [];
  for (var j = 0; j < templates.length; j++) {
    if (templates[j] && templates[j].logoUrl) urls.push(templates[j].logoUrl);
  }
  return urls;
}

function LogoPickerPopup(props) {
  var currentLogoUrl = props.currentLogoUrl || '';
  var workflowState = props.workflowState;
  var customObjectTemplates = props.customObjectTemplates;
  var onPick = props.onPick || function() {};
  var onClose = props.onClose || function() {};

  var [serverUrls, setServerUrls] = useState([]);
  var [localUrls, setLocalUrls] = useState(readLocalUrls);
  var [query, setQuery] = useState('');
  var [newUrl, setNewUrl] = useState('');
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    var cancelled = false;
    loadNodeLogoCatalog()
      .then(function(urls) {
        if (cancelled) return;
        setServerUrls(Array.isArray(urls) ? urls : []);
      })
      .catch(function() { if (!cancelled) setServerUrls([]); })
      .then(function() { if (!cancelled) setLoading(false); });
    return function() { cancelled = true; };
  }, []);

  useEffect(function() {
    function onKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return function() { document.removeEventListener('keydown', onKeyDown); };
  }, [onClose]);

  var allUrls = useMemo(function() {
    var inMemory = collectInMemoryUrls(workflowState, customObjectTemplates);
    return dedupe(inMemory.concat(serverUrls).concat(localUrls));
  }, [serverUrls, localUrls, workflowState, customObjectTemplates]);

  var filtered = useMemo(function() {
    var q = query.trim().toLowerCase();
    if (!q) return allUrls;
    return allUrls.filter(function(u) { return u.toLowerCase().indexOf(q) !== -1; });
  }, [allUrls, query]);

  var handleAdd = useCallback(function() {
    var url = newUrl.trim();
    if (!URL_REGEX.test(url)) {
      setError('Enter a valid http(s) URL');
      return;
    }
    setError('');
    var next = dedupe([url].concat(localUrls));
    setLocalUrls(next);
    writeLocalUrls(next);
    setNewUrl('');
    onPick(url);
  }, [newUrl, localUrls, onPick]);

  var handleRemoveLocal = useCallback(function(url, event) {
    event.stopPropagation();
    var next = localUrls.filter(function(u) { return u !== url; });
    setLocalUrls(next);
    writeLocalUrls(next);
  }, [localUrls]);

  var handleClear = useCallback(function() {
    onPick('');
  }, [onPick]);

  return h('div', { className: 'logo-picker-overlay', onMouseDown: onClose },
    h('div', {
      className: 'logo-picker-box',
      onMouseDown: function(e) { e.stopPropagation(); }
    },
      h('div', { className: 'logo-picker-header' },
        h('h3', { className: 'logo-picker-title' }, 'Pick a node logo'),
        h('button', { className: 'logo-picker-close', type: 'button', onClick: onClose, 'aria-label': 'Close' }, '×')
      ),
      h('div', { className: 'logo-picker-search' },
        h('input', {
          type: 'search',
          className: 'logo-picker-search-input',
          placeholder: 'Filter by URL…',
          value: query,
          onChange: function(e) { setQuery(e.target.value); },
          autoFocus: true
        })
      ),
      h('div', { className: 'logo-picker-grid', role: 'list' },
        loading
          ? h('div', { className: 'logo-picker-loading' }, 'Loading logos…')
          : filtered.length === 0
            ? h('div', { className: 'logo-picker-empty' }, query ? 'No logos match your search.' : 'No logos found yet — add one below.')
            : filtered.map(function(url) {
                var isActive = url === currentLogoUrl;
                var isLocal = localUrls.indexOf(url) !== -1;
                return h('button', {
                  key: url,
                  type: 'button',
                  className: 'logo-picker-tile' + (isActive ? ' logo-picker-tile-active' : ''),
                  title: url,
                  onClick: function() { onPick(url); }
                },
                  h('img', {
                    src: url,
                    alt: '',
                    className: 'logo-picker-tile-img',
                    onError: function(e) { e.target.style.opacity = '0.2'; }
                  }),
                  isLocal
                    ? h('span', {
                        className: 'logo-picker-tile-remove',
                        title: 'Remove from your saved list',
                        onClick: function(e) { handleRemoveLocal(url, e); }
                      }, '×')
                    : null
                );
              })
      ),
      h('div', { className: 'logo-picker-add' },
        h('input', {
          type: 'url',
          className: 'logo-picker-add-input',
          placeholder: 'https://example.com/logo.png',
          value: newUrl,
          onChange: function(e) { setNewUrl(e.target.value); setError(''); },
          onKeyDown: function(e) { if (e.key === 'Enter') handleAdd(); }
        }),
        h('button', {
          type: 'button',
          className: 'logo-picker-add-btn',
          onClick: handleAdd,
          disabled: !newUrl.trim()
        }, 'Add & use')
      ),
      error ? h('div', { className: 'logo-picker-error' }, error) : null,
      h('div', { className: 'logo-picker-footer' },
        currentLogoUrl
          ? h('button', { type: 'button', className: 'logo-picker-clear', onClick: handleClear }, 'Remove logo from this node')
          : null,
        h('span', { className: 'logo-picker-footer-hint' }, filtered.length + ' logo' + (filtered.length === 1 ? '' : 's') + ' available')
      )
    )
  );
}

module.exports = LogoPickerPopup;
